import { wallIsCartesian, wallIsXCartesian, wallIsYCartesian } from '@geometry'
import produce from 'immer'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

export type IntelligentConnectedEnd = 'Start' | 'End'

export type IntelligentConnectedWall = {
  wall: ShapeObject
  end: IntelligentConnectedEnd
}

const wallsArePerpendicular = (a: ShapeObject, b: ShapeObject): boolean =>
  (wallIsXCartesian(a) && wallIsYCartesian(b)) || (wallIsYCartesian(a) && wallIsXCartesian(b))

/**
 * Exact XY membership of a point on a cartesian wall axis (no distance epsilon).
 * A near miss is intentionally treated as not connected.
 */
export const isPointExactlyOnCartesianWall = (
  point: { x: number; y: number },
  wall: ShapeObject,
): boolean => {
  const [start, end] = wall.shape.points

  if (wallIsXCartesian(wall)) {
    if (point.y !== start.y) return false
    const minX = Math.min(start.x, end.x)
    const maxX = Math.max(start.x, end.x)
    return point.x >= minX && point.x <= maxX
  }

  if (wallIsYCartesian(wall)) {
    if (point.x !== start.x) return false
    const minY = Math.min(start.y, end.y)
    const maxY = Math.max(start.y, end.y)
    return point.y >= minY && point.y <= maxY
  }

  return false
}

/**
 * Walls that should shorten/lengthen when `selectedWall` is translated orthogonally:
 * same storey, cartesian, perpendicular, with start or end exactly on the selected wall.
 */
export const findIntelligentConnectedWalls = (
  selectedWall: ShapeObject,
  walls: ShapeObject[],
): IntelligentConnectedWall[] => {
  if (!wallIsCartesian(selectedWall)) return []

  const connected: IntelligentConnectedWall[] = []

  walls.forEach(wall => {
    if (wall.guid === selectedWall.guid) return
    if (wall.storey !== selectedWall.storey) return
    if (!wallIsCartesian(wall)) return
    if (!wallsArePerpendicular(selectedWall, wall)) return

    const [start, end] = wall.shape.points
    const startOn = isPointExactlyOnCartesianWall(start, selectedWall)
    const endOn = isPointExactlyOnCartesianWall(end, selectedWall)

    // Perpendicular walls cannot have both ends on a single straight wall unless zero-length.
    if (startOn && !endOn) {
      connected.push({ wall, end: 'Start' })
    } else if (endOn && !startOn) {
      connected.push({ wall, end: 'End' })
    }
  })

  return connected
}

/**
 * Moves the connected end by `delta`. Returns null when the result would have length <= 0.
 */
export const applyIntelligentTranslation = (
  connected: IntelligentConnectedWall,
  delta: ImmutableVector3,
): ShapeObject | null => {
  const { wall, end } = connected
  const [start, endPoint] = wall.shape.points

  const movedStart =
    end === 'Start' ? new ImmutableVector3(start.x + delta.x, start.y + delta.y, start.z) : start
  const movedEnd =
    end === 'End'
      ? new ImmutableVector3(endPoint.x + delta.x, endPoint.y + delta.y, endPoint.z)
      : endPoint

  const length = Math.hypot(movedEnd.x - movedStart.x, movedEnd.y - movedStart.y)
  if (length <= 0) return null

  return produce(draft => {
    const last = draft.shape.points.length - 1
    draft.shape.points[0] = movedStart
    draft.shape.points[1] = movedEnd
    draft.shape.points[2] = new ImmutableVector3(movedEnd.x, movedEnd.y, draft.shape.points[2].z)
    draft.shape.points[last] = new ImmutableVector3(
      movedStart.x,
      movedStart.y,
      draft.shape.points[last].z,
    )
    draft.is_updated = end
    draft.is_translated = undefined
  }, wall)()
}

/** Preview axis endpoints after applying `delta`, or null if length would collapse. */
export const getIntelligentPreviewLine = (
  connected: IntelligentConnectedWall,
  delta: ImmutableVector3,
): [ImmutableVector3, ImmutableVector3] | null => {
  const updated = applyIntelligentTranslation(connected, delta)
  if (!updated) return null
  return [updated.shape.points[0], updated.shape.points[1]]
}
